import { Button, Group, Stack, Table, Text } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import type { FailoverEventDto } from "@/types/ai-assistant-failover-health";
import { fetchFailoverEvents } from "./failover-health.api";
import { formatWita, reasonText, slotLabel } from "./failover-insights.logic";

interface Props {
	/** Berubah bila status dimuat ulang, memicu muat ulang halaman pertama. */
	reloadWhen: unknown;
}

/** Riwayat pindah ke Cadangan 30 hari terakhir: hanya metadata, tanpa isi percakapan. */
export function FailoverHistoryTable({ reloadWhen }: Props) {
	const [events, setEvents] = useState<FailoverEventDto[] | null>(null);
	const [cursor, setCursor] = useState<string | null>(null);
	const [retentionDays, setRetentionDays] = useState<number | null>(null);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reloadWhen sengaja jadi pemicu muat ulang.
	useEffect(() => {
		const controller = new AbortController();
		fetchFailoverEvents(undefined, controller.signal)
			.then((page) => {
				setEvents(page.events);
				setCursor(page.nextCursor);
				setRetentionDays(page.retentionDays);
			})
			.catch((err: unknown) => {
				if (err instanceof Error && err.name === "AbortError") return;
				setError(err instanceof Error ? err.message : "Gagal memuat riwayat");
			});
		return () => controller.abort();
	}, [reloadWhen]);

	const loadMore = useCallback(async () => {
		if (!cursor) return;
		setLoadingMore(true);
		try {
			const page = await fetchFailoverEvents(cursor);
			setEvents((prev) => [...(prev ?? []), ...page.events]);
			setCursor(page.nextCursor);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Gagal memuat riwayat");
		} finally {
			setLoadingMore(false);
		}
	}, [cursor]);

	if (!events) {
		return error ? (
			<Text size="sm" c="red">
				{error}
			</Text>
		) : null;
	}

	return (
		<Stack gap="xs" data-failover-history>
			<Text fw={600} size="sm">
				Riwayat pindah ke Cadangan
				{retentionDays ? ` (${retentionDays} hari terakhir)` : ""}
			</Text>
			{events.length === 0 ? (
				<Text size="sm" c="dimmed" data-failover-history-empty>
					Belum ada giliran yang dipindah ke Cadangan.
				</Text>
			) : (
				<Table.ScrollContainer minWidth={640}>
					<Table striped verticalSpacing="xs">
						<Table.Thead>
							<Table.Tr>
								<Table.Th>Waktu</Table.Th>
								<Table.Th>Slot</Table.Th>
								<Table.Th>Model</Table.Th>
								<Table.Th>Alasan</Table.Th>
								<Table.Th>Jenis</Table.Th>
							</Table.Tr>
						</Table.Thead>
						<Table.Tbody>
							{events.map((e) => (
								<Table.Tr key={e.id}>
									<Table.Td>{formatWita(e.at)}</Table.Td>
									<Table.Td>
										{slotLabel(e.fromSlot)} → {slotLabel(e.toSlot)}
									</Table.Td>
									<Table.Td>
										{e.fromModel} → {e.toModel}
									</Table.Td>
									<Table.Td>{reasonText(e.reason)}</Table.Td>
									<Table.Td>
										{e.modality === "voice" ? "Suara" : "Teks"}
									</Table.Td>
								</Table.Tr>
							))}
						</Table.Tbody>
					</Table>
				</Table.ScrollContainer>
			)}
			{error && (
				<Text size="sm" c="red">
					{error}
				</Text>
			)}
			{cursor && (
				<Group>
					<Button
						variant="light"
						size="xs"
						loading={loadingMore}
						onClick={loadMore}
					>
						Muat lebih banyak
					</Button>
				</Group>
			)}
		</Stack>
	);
}
